import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * ConfirmModal — riusabile modale di conferma con design coerente.
 * Sostituisce window.confirm() con un'UX consistente con il pannello.
 */
export function ConfirmModal({
  open,
  title = "Sei sicuro?",
  message,
  confirmLabel = "Conferma",
  cancelLabel = "Annulla",
  tone = "danger", // "danger" | "primary"
  onConfirm,
  onCancel,
  busy = false,
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmCls =
    tone === "danger"
      ? "bg-rose-600 hover:bg-rose-700"
      : "bg-emerald-600 hover:bg-emerald-700";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="confirm-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in"
        onClick={busy ? undefined : onCancel}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40"
          data-testid="modal-close"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="text-lg font-semibold text-slate-900 pr-8">{title}</h3>
        {message && (
          <div className="mt-2 text-sm text-slate-600 whitespace-pre-line">{message}</div>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            data-testid="modal-cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-full text-white px-5 py-2 text-sm font-medium transition ${confirmCls} disabled:opacity-40`}
            data-testid="modal-confirm"
          >
            {busy && (
              <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeDasharray="42" />
              </svg>
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
